import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2 } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import LandingNav from '../components/landing/LandingNav';
import FooterSection from '../components/landing/FooterSection';
import LinkedInAdsHero from './linkedin-ads-landing/LinkedInAdsHero';
import LinkedInAdsBenefits from './linkedin-ads-landing/LinkedInAdsBenefits';
import LinkedInAdsForm from './linkedin-ads-landing/LinkedInAdsForm';

const LinkedInAdsLanding = ({ defaultLocale }) => {
  const { t, i18n } = useTranslation();
  const [searchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);

  // Derive audience from search params
  const rawAudience = searchParams.get('audience') || '';
  const audience = (rawAudience === 'smb' || rawAudience === 'vendor') ? rawAudience : 'general';

  useEffect(() => {
    if (defaultLocale) {
      i18n.changeLanguage(defaultLocale);
    }
  }, []); // mount-only: set locale from URL path, not user preference

  useEffect(() => {
    const lng = searchParams.get('lng');
    if (lng && ['en', 'fr'].includes(lng)) {
      i18n.changeLanguage(lng);
    }

    if (window.posthog) {
      window.posthog.capture('linkedin_landing_viewed', {
        segment: audience,
        lang: i18n.language,
        utm_source: searchParams.get('utm_source') || undefined,
        utm_medium: searchParams.get('utm_medium') || undefined,
        utm_campaign: searchParams.get('utm_campaign') || undefined,
      });
    }
  }, [searchParams, i18n, audience]);

  if (submitted) {
    return (
      <div className="min-h-screen bg-background">
        <SEOHead
          title={t('linkedin.meta.titleSuccess')}
          description={t('linkedin.meta.description')}
          url="https://proposai.ca/linkedin"
          noIndex={true}
          lang={i18n.language}
        />
        <LandingNav />
        <div className="pt-32 pb-20 px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl mx-auto text-center">
            <div className="mb-6">
              <CheckCircle2 className="w-16 h-16 text-primary mx-auto" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4">
              {t('linkedin.success.title')}
            </h1>
            <p className="text-xl text-muted-foreground mb-8">
              {t('linkedin.success.description')}
            </p>
            <div className="bg-secondary/30 border border-primary/20 rounded-lg p-6">
              <p className="text-foreground">
                {t('linkedin.success.nextSteps')}
              </p>
            </div>
          </div>
        </div>
        <FooterSection />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title={t('linkedin.meta.title')}
        description={t('linkedin.meta.description')}
        url="https://proposai.ca/linkedin"
        noIndex={true}
        lang={i18n.language}
      />

      <LandingNav />

      <LinkedInAdsHero audience={audience} searchParams={searchParams} />
      <LinkedInAdsBenefits audience={audience} />
      <LinkedInAdsForm
        audience={audience}
        searchParams={searchParams}
        onSuccess={() => setSubmitted(true)}
      />

      <FooterSection />
    </div>
  );
};

export default LinkedInAdsLanding;
